import type { VariantProps } from 'cva'
import { XIcon } from 'lucide-react'

import { cn, cva } from '~/utils/classnames'

import { IconButton } from './button'
import { Modal } from './modal'

type DrawerProps = React.ComponentProps<typeof Modal>

const DrawerRoot = (props: DrawerProps) => {
  return <Modal {...props} />
}

type DrawerContentProps = React.ComponentProps<typeof Modal.Content> & {
  /**
   * Controls the drawer's slide direction and positioning.
   * - 'right': Slides from right on desktop, bottom on mobile (default)
   * - 'bottom': Slides from bottom on all screen sizes, full-width
   * @default 'right'
   */
  direction?: VariantProps<typeof style>['direction']
}

const style = cva({
  base: [
    '[--drawer-px:--spacing(4)] [--drawer-py:--spacing(4)] md:[--drawer-px:--spacing(5)]',
    'bg-background border-border/4 flex w-full flex-col overflow-x-hidden overflow-y-auto border *:shrink-0',
    // mobile
    'min-h-[80svh] max-h-[calc(100svh-(--spacing(16)))] w-full rounded-t-xl',
    'max-w-screen mb-0 max-md:mt-auto!',
    // desktop
    'md:mt-2 md:mr-2 md:mb-2 md:h-auto md:max-h-screen md:w-full md:max-w-154 md:rounded-xl',
    // backdrop
    'backdrop:bg-under/40 backdrop:backdrop-blur-sm not-data-[status=open]:backdrop:opacity-0',
    // animation props
    'ease-emphasized-decelerate not-data-[status=open]:ease-emphasized-accelerate transition-transform',
    'backdrop:transition-opacity backdrop:ease-in-out',
    'motion-reduce:transition-none motion-reduce:backdrop:transition-none',
    'duration-400 not-data-[status=open]:duration-250 not-data-[status=open]:backdrop:delay-150 not-data-[status=open]:backdrop:duration-200',
  ],
  variants: {
    direction: {
      right: [
        'md:not-data-[status=open]:translate-x-full',
        'max-md:not-data-[status=open]:translate-y-full',
      ],
      bottom: [
        'rounded-b-none m-0! mt-16!',
        'not-data-[status=open]:translate-y-full',
        'md:max-w-full',
      ],
    },
  },
})

const DrawerContent = ({
  className,
  children,
  direction = 'right',
  ...props
}: DrawerContentProps) => {
  return (
    <Modal.Content className={style({ direction, className })} {...props}>
      {children}
    </Modal.Content>
  )
}

const DrawerTrigger = Modal.Trigger

const DrawerClose = Modal.Close

const DrawerTitle = ({
  children,
  className,
  ...props
}: React.ComponentProps<typeof Modal.Title>) => {
  return (
    <Modal.Title className={cn('text-base font-medium', className)} {...props}>
      {children}
    </Modal.Title>
  )
}

const DrawerDescription = ({
  children,
  className,
  ...props
}: React.ComponentProps<typeof Modal.Description>) => {
  return (
    <Modal.Description className={cn(className)} {...props}>
      {children}
    </Modal.Description>
  )
}

const DrawerSection = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<'div'>) => {
  return (
    <div
      className={cn('px-(--drawer-px) py-(--drawer-py)', className)}
      {...props}
    >
      {children}
    </div>
  )
}

const DrawerBleed = ({
  className,
  ...props
}: React.ComponentPropsWithRef<'div'>) => {
  return <div className={cn('-mx-(--drawer-px)', className)} {...props} />
}

const DrawerHeader = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<'div'>) => {
  return (
    <DrawerSection
      className={cn(
        'bg-background border-border/4 sticky top-0 z-10 min-h-13 border-b pr-[calc((--spacing(9))+var(--drawer-px))]',
        className
      )}
      {...props}
    >
      {children}
      <div className="absolute top-0 right-0 flex h-full items-center px-3">
        <DrawerClose asChild>
          <IconButton aria-label="Close drawer" variant="ghost" size="md">
            <XIcon className="size-4.5" />
          </IconButton>
        </DrawerClose>
      </div>
    </DrawerSection>
  )
}

const DrawerActions = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<'div'>) => {
  return (
    <DrawerSection
      className={cn(
        'bg-background border-border/4 sticky bottom-0 mt-auto flex gap-2 border-t',
        className
      )}
      {...props}
    >
      {children}
    </DrawerSection>
  )
}

const Drawer = Object.assign(DrawerRoot, {
  Content: DrawerContent,
  Section: DrawerSection,
  Trigger: DrawerTrigger,
  Close: DrawerClose,
  Title: DrawerTitle,
  Description: DrawerDescription,
  Bleed: DrawerBleed,
  Header: DrawerHeader,
  Actions: DrawerActions,
})

export { Drawer }
